import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { BoltIcon } from "../components/Bolts";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "../components/ui/tabs";
import { COLLECTIONS } from "../data/catalog";
import {
    getAllCategories,
    findCategoryMerged,
    createCategory,
    createSubcategory,
    getAllThemes,
    createTheme,
} from "../lib/dynamicCatalog";
import {
    Upload,
    Wand2,
    Sparkles,
    Download,
    Trash2,
    RefreshCw,
    User as UserIcon,
    Image as ImageIcon,
    ArrowLeft,
    X,
    CheckCircle2,
    AlertCircle,
    Loader2,
    Eye,
    EyeOff,
    Pencil,
    Save,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tabs = [
    {
        key: "upload",
        label: "Upload Existing",
        icon: <Upload className="w-4 h-4" />,
        title: "Add a photo you already have",
        sub: "Already have good product photos? Upload them here as-is — no AI step. Tag them and they're ready to list.",
        endpoint: "/photos/upload",
        needsFile: true,
        placeholderPrompt: null,
    },
    {
        key: "enhance",
        label: "Enhance Photos",
        icon: <Sparkles className="w-4 h-4" />,
        title: "Make my pictures better",
        sub: "Upload one or many photos — we'll clean them up, sharpen, color-correct, and place each on a premium backdrop.",
        endpoint: "/photos/enhance",
        needsFile: true,
        placeholderPrompt: "Optional: e.g. 'shoot on wet concrete with rim lighting'",
    },
    {
        key: "mockup",
        label: "Place on AI Model",
        icon: <UserIcon className="w-4 h-4" />,
        title: "Put my shirts on AI models",
        sub: "Upload one or many flat-lay shirt photos — we'll wrap each on a realistic model in a stormy editorial scene.",
        endpoint: "/photos/mockup",
        needsFile: true,
        placeholderPrompt: "Optional: e.g. 'male model, leather jacket, rain in background'",
    },
    {
        key: "both",
        label: "Both",
        icon: (
            <span className="inline-flex items-center -space-x-1">
                <Sparkles className="w-3.5 h-3.5" />
                <UserIcon className="w-3.5 h-3.5" />
            </span>
        ),
        title: "Studio shot AND AI model, in one pass",
        sub: "Upload one or many flat-lay shirt photos — for each one, we'll generate both a clean studio shot and a realistic AI-model mockup.",
        endpoint: null,
        needsFile: true,
        placeholderPrompt: "Optional: e.g. 'male model, leather jacket, rain in background'",
    },
    {
        key: "generate",
        label: "Generate From Prompt",
        icon: <Wand2 className="w-4 h-4" />,
        title: "Generate from text",
        sub: "Describe an image and Nano Banana will create it.",
        endpoint: "/photos/generate",
        needsFile: false,
        placeholderPrompt: "e.g. 'a hooded figure standing in a desert lightning storm, cinematic, moody'",
    },
];

// Job statuses: pending, processing, done, error
const PhotoStudioPage = () => {
    const [active, setActive] = useState("enhance");
    const [jobs, setJobs] = useState([]); // [{id, file, previewUrl, status, resultId?, error?}]
    const [prompt, setPrompt] = useState("");
    const [busy, setBusy] = useState(false);
    const [history, setHistory] = useState([]);
    const [categories, setCategories] = useState(() => getAllCategories());
    const [themes, setThemes] = useState(() => getAllThemes());
    const inputRef = useRef(null);

    // Re-reads the merged (builtin + custom) category list after a new
    // category/subcategory is created, so every dropdown on this page updates.
    const refreshCategoriesState = () => {
        setCategories(getAllCategories());
        setThemes(getAllThemes());
    };

    // "+ Add new theme…" — prompts for a name, creates it, returns its slug.
    const handleAddTheme = async () => {
        const name = window.prompt('Name your new theme (e.g. "Star Wars"):');
        if (!name || !name.trim()) return null;
        try {
            const created = await createTheme(name.trim());
            refreshCategoriesState();
            toast.success(`Theme "${created.name}" created.`);
            return created.slug;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't create theme.");
            return null;
        }
    };

    // "+ Add new category…" — prompts for a name, creates it (its own real
    // page on the site, same as Apparel or Woodworking), and returns its slug.
    const handleAddCategory = async () => {
        const name = window.prompt('Name your new category (e.g. "Phone Cases"):');
        if (!name || !name.trim()) return null;
        try {
            const created = await createCategory(name.trim());
            refreshCategoriesState();
            toast.success(`Category "${created.name}" created.`);
            return created.slug;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't create category.");
            return null;
        }
    };

    // "+ Add new sub-category…" — same idea, nested under an existing category.
    const handleAddSubcategory = async (categorySlug) => {
        const name = window.prompt('Name your new sub-category (e.g. "Holographic"):');
        if (!name || !name.trim()) return null;
        try {
            const created = await createSubcategory(categorySlug, name.trim());
            refreshCategoriesState();
            toast.success(`Sub-category "${created.name}" created.`);
            return created.slug;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't create sub-category.");
            return null;
        }
    };

    const tab = tabs.find((t) => t.key === active);

    const loadHistory = async () => {
        try {
            const { data } = await axios.get(`${API}/photos`);
            setHistory(data || []);
        } catch (e) {
            // silent
        }
    };

    useEffect(() => {
        loadHistory();
    }, []);

    useEffect(() => {
        // Reset queue & prompt on tab change
        setJobs([]);
        setPrompt("");
        if (inputRef.current) inputRef.current.value = "";
    }, [active]);

    const addFiles = (fileList) => {
        const incoming = Array.from(fileList || []).filter((f) => f && f.type.startsWith("image/"));
        if (incoming.length === 0) {
            toast.error("Please upload image files (PNG/JPG/HEIC/WEBP).");
            return;
        }
        const newJobs = incoming.map((f, idx) => ({
            id: `${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`,
            file: f,
            previewUrl: URL.createObjectURL(f),
            status: "pending",
            resultId: null,
            error: null,
            category: "apparel",
        }));
        setJobs((prev) => [...prev, ...newJobs]);
    };

    const removeJob = (jobId) => {
        setJobs((prev) => {
            const removed = prev.find((j) => j.id === jobId);
            if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
            return prev.filter((j) => j.id !== jobId);
        });
    };

    const clearAll = () => {
        jobs.forEach((j) => j.previewUrl && URL.revokeObjectURL(j.previewUrl));
        setJobs([]);
        setPrompt("");
        if (inputRef.current) inputRef.current.value = "";
    };

    const onDrop = (e) => {
        e.preventDefault();
        if (tab.needsFile) addFiles(e.dataTransfer.files);
    };

    const updateJob = (jobId, patch) =>
        setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, ...patch } : j)));

    const submit = async () => {
        if (tab.needsFile) {
            if (jobs.length === 0) {
                toast.error("Upload at least one photo first.");
                return;
            }
        } else {
            if (!prompt.trim()) {
                toast.error("Write a prompt first.");
                return;
            }
        }
        setBusy(true);
        try {
            if (!tab.needsFile) {
                // Single generate-from-prompt call
                await axios.post(
                    `${API}${tab.endpoint}`,
                    { prompt: prompt.trim() },
                    { timeout: 180000 }
                );
                toast.success("Image generated.");
                setPrompt("");
                loadHistory();
                return;
            }

            // Batch: process each job sequentially
            let okCount = 0;
            let failCount = 0;
            const pending = jobs.filter((j) => j.status !== "done");
            // "Both" runs the two real endpoints back-to-back for every upload
            const endpointsToRun = active === "both" ? ["/photos/enhance", "/photos/mockup"] : [tab.endpoint];
            for (const job of pending) {
                updateJob(job.id, { status: "processing", error: null });
                try {
                    for (const ep of endpointsToRun) {
                        const form = new FormData();
                        form.append("image", job.file);
                        if (prompt.trim()) form.append("extra_prompt", prompt.trim());
                        if (job.category) form.append("category", job.category);
                        const { data } = await axios.post(`${API}${ep}`, form, {
                            headers: { "Content-Type": "multipart/form-data" },
                            timeout: 180000,
                        });
                        updateJob(job.id, { status: "done", resultId: data.id });
                        okCount++;
                        // Refresh history incrementally so the user sees results land
                        loadHistory();
                    }
                } catch (err) {
                    const detail = err?.response?.data?.detail || "Generation failed.";
                    updateJob(job.id, {
                        status: "error",
                        error: typeof detail === "string" ? detail : "Generation failed.",
                    });
                    failCount++;
                }
            }
            if (okCount && !failCount) toast.success(`Done — ${okCount} image${okCount > 1 ? "s" : ""} generated.`);
            else if (okCount && failCount) toast.success(`${okCount} done, ${failCount} failed.`);
            else toast.error(`All ${failCount} failed.`);
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${API}/photos/${id}`);
            toast.success("Deleted.");
            loadHistory();
        } catch {
            toast.error("Delete failed.");
        }
    };

    const downloadUrl = (id, variant = "result") =>
        `${API}/photos/${id}/image?variant=${variant}`;

    const pendingCount = jobs.filter((j) => j.status === "pending").length;
    const doneCount = jobs.filter((j) => j.status === "done").length;

    return (
        <div data-testid="photo-studio-page" className="min-h-[80vh]">
            {/* Header */}
            <section className="border-b border-white/10 bg-storm-navy">
                <div className="container-x py-14 md:py-20">
                    <Link
                        to="/admin"
                        data-testid="back-to-admin"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-storm-bolt mb-5 hover:gap-3 transition-all"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to Admin
                    </Link>
                    <p className="eyebrow flex items-center gap-2 mb-4">
                        <BoltIcon className="w-3.5 h-3.5" /> Photo Studio · Powered by Nano Banana
                    </p>
                    <h1 className="text-4xl md:text-6xl text-white leading-[0.9]">
                        Make your photos <br />
                        <span className="text-storm-bolt">storm-grade.</span>
                    </h1>
                    <p className="text-storm-mist mt-4 max-w-2xl">
                        Drop in <span className="text-storm-bolt">one or many</span> photos at once. The
                        AI re-shoots each one. Upload anytime — the tool is live now.
                    </p>
                </div>
            </section>

            <section className="container-x py-10 md:py-14">
                {/* Tabs */}
                <Tabs value={active} onValueChange={setActive} className="w-full">
                    <TabsList className="bg-storm-navy border border-white/10 rounded-none p-0 h-auto gap-0 mb-8 flex flex-wrap">
                        {tabs.map((t) => (
                            <TabsTrigger
                                key={t.key}
                                value={t.key}
                                data-testid={`ps-tab-${t.key}`}
                                className="font-heading uppercase tracking-wider text-sm rounded-none data-[state=active]:bg-storm-bolt data-[state=active]:text-black px-5 py-3 text-white/70 flex items-center gap-2"
                            >
                                {t.icon} {t.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    {tabs.map((t) => (
                        <TabsContent key={t.key} value={t.key} className="mt-0">
                            <div className="surface-solid p-6 md:p-8 space-y-6">
                                <div>
                                    <h2 className="text-2xl md:text-3xl text-white mb-2">{t.title}</h2>
                                    <p className="text-storm-mist text-sm leading-relaxed">{t.sub}</p>
                                </div>

                                {t.needsFile && (
                                    <>
                                        <div
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={onDrop}
                                            onClick={() => inputRef.current?.click()}
                                            data-testid="ps-drop-zone"
                                            className="border-2 border-dashed border-white/15 hover:border-storm-bolt transition-colors cursor-pointer p-8 text-center bg-storm-void/50"
                                        >
                                            <div className="flex flex-col items-center gap-3 text-storm-mist">
                                                <Upload className="w-10 h-10 text-storm-bolt" />
                                                <p className="text-sm">
                                                    Drag &amp; drop <span className="text-white">one or many</span>, or{" "}
                                                    <span className="text-storm-bolt underline-offset-2 underline">
                                                        click to upload
                                                    </span>
                                                </p>
                                                <p className="text-[11px] uppercase tracking-[0.2em]">
                                                    PNG · JPG · HEIC · WEBP · multi-select supported
                                                </p>
                                            </div>
                                            <input
                                                ref={inputRef}
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                hidden
                                                onChange={(e) => {
                                                    addFiles(e.target.files);
                                                    e.target.value = "";
                                                }}
                                                data-testid="ps-file-input"
                                            />
                                        </div>

                                        {jobs.length > 0 && (
                                            <div className="space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <p className="eyebrow">
                                                        Queue · {jobs.length} photo{jobs.length > 1 ? "s" : ""}
                                                        {pendingCount > 0 && ` · ${pendingCount} pending`}
                                                        {doneCount > 0 && ` · ${doneCount} done`}
                                                    </p>
                                                    <button
                                                        onClick={clearAll}
                                                        disabled={busy}
                                                        className="text-xs uppercase tracking-[0.25em] text-storm-mist hover:text-storm-bolt disabled:opacity-50"
                                                        data-testid="ps-clear-queue"
                                                    >
                                                        Clear all
                                                    </button>
                                                </div>
                                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                                                    {jobs.map((j) => (
                                                        <JobCard
                                                            key={j.id}
                                                            job={j}
                                                            onRemove={() => removeJob(j.id)}
                                                            onCategoryChange={(cat) => updateJob(j.id, { category: cat })}
                                                            onAddCategory={handleAddCategory}
                                                            categories={categories}
                                                            resultUrl={j.resultId ? downloadUrl(j.resultId) : null}
                                                            busy={busy}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {active !== "upload" && (
                                    <div>
                                        <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">
                                            {t.needsFile ? "Extra direction — applies to every photo (optional)" : "Prompt *"}
                                        </label>
                                        <Textarea
                                            value={prompt}
                                            onChange={(e) => setPrompt(e.target.value)}
                                            rows={3}
                                            placeholder={t.placeholderPrompt}
                                            data-testid="ps-prompt"
                                            className="bg-storm-void border-white/10 text-white placeholder:text-storm-mist/60 rounded-none focus-visible:ring-storm-bolt focus-visible:ring-offset-0 focus-visible:border-storm-bolt resize-none"
                                        />
                                    </div>
                                )}

                                <div className="flex flex-wrap gap-3">
                                    <button
                                        onClick={submit}
                                        disabled={busy || (t.needsFile && jobs.length === 0) || (!t.needsFile && !prompt.trim())}
                                        data-testid="ps-submit"
                                        className="btn-primary justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {busy ? (
                                            <>
                                                <RefreshCw className="w-4 h-4 animate-spin" /> Working…
                                            </>
                                        ) : t.needsFile ? (
                                            <>
                                                <Sparkles className="w-4 h-4" />
                                                Process {jobs.length > 0 ? `${jobs.length} Photo${jobs.length > 1 ? "s" : ""}` : "Photos"}
                                            </>
                                        ) : (
                                            <>
                                                <Sparkles className="w-4 h-4" /> Generate
                                            </>
                                        )}
                                    </button>
                                    {t.needsFile && jobs.length > 0 && (
                                        <button
                                            onClick={clearAll}
                                            disabled={busy}
                                            className="btn-secondary"
                                            data-testid="ps-reset"
                                        >
                                            Reset
                                        </button>
                                    )}
                                </div>

                                <p className="text-[11px] text-storm-mist leading-relaxed">
                                    Tip: Each photo takes ~10-30 seconds. They run one at a time so
                                    Nano Banana stays sharp. Results land in the gallery below as
                                    they finish — download or delete from there.
                                </p>
                            </div>
                        </TabsContent>
                    ))}
                </Tabs>

                {/* History */}
                <div className="mt-16">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                        <div>
                            <h2 className="text-2xl md:text-3xl text-white">Your Photo Library</h2>
                            <p className="text-xs text-storm-mist mt-1">
                                Tag each photo with a collection &amp; toggle <span className="text-storm-bolt">Publish</span> to make it appear on the T-Shirts page.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={async () => {
                                    if (!window.confirm(`Mark all ${history.length} photos as Published?`)) return;
                                    let ok = 0, fail = 0;
                                    for (const p of history) {
                                        try {
                                            await axios.patch(`${API}/photos/${p.id}`, { published: true });
                                            ok++;
                                        } catch { fail++; }
                                    }
                                    toast.success(`Published ${ok}${fail ? `, ${fail} failed` : ""}.`);
                                    loadHistory();
                                }}
                                className="text-xs uppercase tracking-[0.25em] text-storm-bolt hover:text-white border border-storm-bolt/40 px-3 py-1.5 hover:border-storm-bolt transition-colors"
                                data-testid="ps-publish-all"
                                title="Mark every photo in your library as published"
                            >
                                Publish All
                            </button>
                            <button
                                onClick={loadHistory}
                                className="text-xs uppercase tracking-[0.25em] text-storm-bolt hover:text-white inline-flex items-center gap-1.5"
                                data-testid="ps-history-refresh"
                            >
                                <RefreshCw className="w-3.5 h-3.5" /> Refresh
                            </button>
                        </div>
                    </div>
                    {history.length === 0 ? (
                        <div className="surface-solid p-10 text-center text-storm-mist text-sm">
                            No generations yet.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {history.map((p) => (
                                <LibraryCard
                                    key={p.id}
                                    photo={p}
                                    categories={categories}
                                    themes={themes}
                                    onAddCategory={handleAddCategory}
                                    onAddSubcategory={handleAddSubcategory}
                                    onAddTheme={handleAddTheme}
                                    onDelete={() => handleDelete(p.id)}
                                    onUpdate={async (patch) => {
                                        try {
                                            const { data } = await axios.patch(`${API}/photos/${p.id}`, patch);
                                            setHistory((prev) => prev.map((x) => (x.id === p.id ? data : x)));
                                            toast.success("Saved.");
                                        } catch {
                                            toast.error("Save failed.");
                                        }
                                    }}
                                    downloadUrl={downloadUrl}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

const LibraryCard = ({ photo, categories, themes, onAddCategory, onAddSubcategory, onAddTheme, onDelete, onUpdate, downloadUrl }) => {
    const [editing, setEditing] = useState(false);
    const [title, setTitle] = useState(photo.title || "");
    const [collection, setCollection] = useState(photo.collection || "none");
    const [category, setCategory] = useState(photo.category || "none");
    const [subcategory, setSubcategory] = useState(photo.subcategory || "none");
    const [description, setDescription] = useState(photo.description || "");
    const [priceDollars, setPriceDollars] = useState(
        photo.price_cents != null ? (photo.price_cents / 100).toFixed(2) : ""
    );
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setTitle(photo.title || "");
        setCollection(photo.collection || "none");
        setCategory(photo.category || "none");
        setSubcategory(photo.subcategory || "none");
        setDescription(photo.description || "");
        setPriceDollars(photo.price_cents != null ? (photo.price_cents / 100).toFixed(2) : "");
    }, [photo.id, photo.title, photo.collection, photo.category, photo.subcategory, photo.description, photo.price_cents]);

    const catObj = categories.find((c) => c.slug === category) || null;
    const subOptions = catObj?.sub || [];

    const save = async () => {
        setSaving(true);
        const priceCents = priceDollars.trim() ? Math.round(parseFloat(priceDollars) * 100) : 0;
        await onUpdate({
            title: title.trim(),
            collection: collection === "none" ? "" : collection,
            category: category === "none" ? "" : category,
            subcategory: subcategory === "none" ? "" : subcategory,
            description: description.trim(),
            price_cents: isNaN(priceCents) ? 0 : priceCents,
        });
        setSaving(false);
        setEditing(false);
    };

    const togglePublish = async () => {
        await onUpdate({ published: !photo.published });
    };

    return (
        <div
            className="surface-solid group relative overflow-hidden flex flex-col"
            data-testid={`ps-history-${photo.id}`}
        >
            <div className="aspect-square overflow-hidden bg-storm-void relative">
                <img
                    src={downloadUrl(photo.id)}
                    alt={photo.title || photo.kind}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                />
                <div className="absolute top-2 left-2 flex flex-wrap gap-1.5">
                    {photo.published ? (
                        <span className="inline-flex items-center gap-1 bg-storm-bolt text-black px-2 py-0.5 text-[9px] uppercase tracking-wider font-heading">
                            <Eye className="w-3 h-3" /> Live
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 bg-storm-void/80 border border-white/15 text-storm-mist px-2 py-0.5 text-[9px] uppercase tracking-wider font-heading">
                            <EyeOff className="w-3 h-3" /> Hidden
                        </span>
                    )}
                    {photo.collection && (
                        <span className="bg-storm-void/85 border border-storm-bolt/40 text-storm-bolt px-2 py-0.5 text-[9px] uppercase tracking-wider font-heading">
                            {themes.find((c) => c.slug === photo.collection)?.name || photo.collection}
                        </span>
                    )}
                </div>
            </div>

            <div className="p-3 flex flex-col gap-2.5 flex-1">
                {editing ? (
                    <>
                        <Input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Title (e.g. Pikachu Storm Tee)"
                            data-testid={`ps-edit-title-${photo.id}`}
                            className="bg-storm-void border-white/10 text-white placeholder:text-storm-mist/50 rounded-none h-9 text-sm focus-visible:ring-storm-bolt focus-visible:ring-offset-0"
                        />
                        <div className="grid grid-cols-2 gap-2">
                            <Select
                                value={category}
                                onValueChange={async (v) => {
                                    if (v === "__new_category__") {
                                        const slug = await onAddCategory();
                                        if (slug) { setCategory(slug); setSubcategory("none"); }
                                        return;
                                    }
                                    setCategory(v);
                                    setSubcategory("none");
                                }}
                            >
                                <SelectTrigger
                                    data-testid={`ps-edit-cat-${photo.id}`}
                                    className="bg-storm-void border-white/10 text-white rounded-none h-9 text-xs"
                                >
                                    <SelectValue placeholder="Category" />
                                </SelectTrigger>
                                <SelectContent className="bg-storm-navy border-white/10 text-white rounded-none">
                                    <SelectItem value="none" className="focus:bg-storm-bolt focus:text-black rounded-none font-heading uppercase tracking-wider text-xs">
                                        None
                                    </SelectItem>
                                    {categories.map((c) => (
                                        <SelectItem
                                            key={c.slug}
                                            value={c.slug}
                                            className="focus:bg-storm-bolt focus:text-black rounded-none font-heading uppercase tracking-wider text-xs"
                                        >
                                            {c.name}
                                        </SelectItem>
                                    ))}
                                    <SelectItem
                                        value="__new_category__"
                                        data-testid={`ps-edit-cat-new-${photo.id}`}
                                        className="focus:bg-storm-bolt focus:text-black rounded-none font-heading uppercase tracking-wider text-xs text-storm-bolt"
                                    >
                                        + Add new category…
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                            <Select
                                value={subcategory}
                                onValueChange={async (v) => {
                                    if (v === "__new_subcategory__") {
                                        const slug = await onAddSubcategory(catObj.slug);
                                        if (slug) setSubcategory(slug);
                                        return;
                                    }
                                    setSubcategory(v);
                                }}
                                disabled={!catObj}
                            >
                                <SelectTrigger
                                    data-testid={`ps-edit-sub-${photo.id}`}
                                    className="bg-storm-void border-white/10 text-white rounded-none h-9 text-xs disabled:opacity-50"
                                >
                                    <SelectValue placeholder={subOptions.length ? "Sub-category" : "—"} />
                                </SelectTrigger>
                                <SelectContent className="bg-storm-navy border-white/10 text-white rounded-none">
                                    <SelectItem value="none" className="focus:bg-storm-bolt focus:text-black rounded-none font-heading uppercase tracking-wider text-xs">
                                        None
                                    </SelectItem>
                                    {subOptions.map((s) => (
                                        <SelectItem
                                            key={s.slug}
                                            value={s.slug}
                                            className="focus:bg-storm-bolt focus:text-black rounded-none font-heading uppercase tracking-wider text-xs"
                                        >
                                            {s.name}
                                        </SelectItem>
                                    ))}
                                    {catObj && (
                                        <SelectItem
                                            value="__new_subcategory__"
                                            data-testid={`ps-edit-sub-new-${photo.id}`}
                                            className="focus:bg-storm-bolt focus:text-black rounded-none font-heading uppercase tracking-wider text-xs text-storm-bolt"
                                        >
                                            + Add new sub-category…
                                        </SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        <Select
                            value={collection}
                            onValueChange={async (v) => {
                                if (v === "__new_theme__") {
                                    const slug = await onAddTheme();
                                    if (slug) setCollection(slug);
                                    return;
                                }
                                setCollection(v);
                            }}
                        >
                            <SelectTrigger
                                data-testid={`ps-edit-coll-${photo.id}`}
                                className="bg-storm-void border-white/10 text-white rounded-none h-9 text-xs"
                            >
                                <SelectValue placeholder="Theme (Pokémon, etc.)" />
                            </SelectTrigger>
                            <SelectContent className="bg-storm-navy border-white/10 text-white rounded-none">
                                <SelectItem value="none" className="focus:bg-storm-bolt focus:text-black rounded-none font-heading uppercase tracking-wider text-xs">
                                    No theme
                                </SelectItem>
                                {themes.map((c) => (
                                    <SelectItem
                                        key={c.slug}
                                        value={c.slug}
                                        className="focus:bg-storm-bolt focus:text-black rounded-none font-heading uppercase tracking-wider text-xs"
                                    >
                                        {c.name}
                                    </SelectItem>
                                ))}
                                <SelectItem
                                    value="__new_theme__"
                                    data-testid={`ps-edit-theme-new-${photo.id}`}
                                    className="focus:bg-storm-bolt focus:text-black rounded-none font-heading uppercase tracking-wider text-xs text-storm-bolt"
                                >
                                    + Add new theme…
                                </SelectItem>
                            </SelectContent>
                        </Select>
                        <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-storm-mist text-sm">$</span>
                            <Input
                                type="text"
                                inputMode="decimal"
                                value={priceDollars}
                                onChange={(e) => setPriceDollars(e.target.value.replace(/[^0-9.]/g, ""))}
                                placeholder="Price (e.g. 17.00)"
                                data-testid={`ps-edit-price-${photo.id}`}
                                className="bg-storm-void border-white/10 text-white placeholder:text-storm-mist/50 rounded-none h-9 text-sm pl-6 focus-visible:ring-storm-bolt focus-visible:ring-offset-0"
                            />
                        </div>
                        <Textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={2}
                            placeholder="Short description (1-2 sentences)"
                            data-testid={`ps-edit-desc-${photo.id}`}
                            className="bg-storm-void border-white/10 text-white placeholder:text-storm-mist/50 rounded-none text-xs focus-visible:ring-storm-bolt focus-visible:ring-offset-0 resize-none"
                        />
                        <div className="flex gap-2">
                            <button
                                onClick={save}
                                disabled={saving}
                                data-testid={`ps-save-${photo.id}`}
                                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-storm-bolt text-black text-[11px] uppercase tracking-wider py-2 font-heading hover:bg-white disabled:opacity-50"
                            >
                                <Save className="w-3.5 h-3.5" /> {saving ? "Saving" : "Save"}
                            </button>
                            <button
                                onClick={() => setEditing(false)}
                                className="px-3 border border-white/10 text-storm-mist hover:text-white text-[11px] uppercase tracking-wider py-2 font-heading"
                            >
                                Cancel
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="flex items-start justify-between gap-2">
                            <h4 className="font-heading text-sm uppercase tracking-wider text-white line-clamp-2 flex-1">
                                {photo.title || `${photo.kind} · ${photo.id.slice(0, 6).toUpperCase()}`}
                            </h4>
                            <span className="text-[10px] text-storm-mist whitespace-nowrap">
                                {new Date(photo.created_at).toLocaleDateString()}
                            </span>
                        </div>
                        <div className="text-[11px] text-storm-mist space-y-0.5 mt-0.5">
                            {photo.category && (
                                <div className="flex items-center gap-1.5">
                                    <span className="text-storm-bolt">▸</span>
                                    <span className="uppercase tracking-wider">
                                        {categories.find((c) => c.slug === photo.category)?.name || photo.category}
                                        {photo.subcategory && ` · ${photo.subcategory}`}
                                    </span>
                                </div>
                            )}
                            {photo.price_cents != null && photo.price_cents > 0 && (
                                <div className="font-heading text-storm-bolt">
                                    ${(photo.price_cents / 100).toFixed(2)}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-auto pt-2">
                            <button
                                onClick={() => setEditing(true)}
                                data-testid={`ps-edit-${photo.id}`}
                                className="flex-1 inline-flex items-center justify-center gap-1.5 border border-white/10 hover:border-storm-bolt hover:text-storm-bolt text-[11px] uppercase tracking-wider py-1.5 transition-colors"
                            >
                                <Pencil className="w-3 h-3" /> Edit
                            </button>
                            <button
                                onClick={togglePublish}
                                data-testid={`ps-publish-${photo.id}`}
                                className={`flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-wider py-1.5 transition-colors border ${
                                    photo.published
                                        ? "border-storm-bolt text-storm-bolt hover:bg-storm-bolt hover:text-black"
                                        : "border-white/10 text-storm-mist hover:border-storm-bolt hover:text-storm-bolt"
                                }`}
                                title={photo.published ? "Hide from site" : "Publish to site"}
                            >
                                {photo.published ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                {photo.published ? "Hide" : "Publish"}
                            </button>
                            <a
                                href={downloadUrl(photo.id)}
                                download={`dangersky-${photo.id}.png`}
                                className="px-2.5 inline-flex items-center justify-center border border-white/10 hover:border-storm-bolt hover:text-storm-bolt transition-colors py-1.5"
                                title="Download"
                            >
                                <Download className="w-3 h-3" />
                            </a>
                            <button
                                onClick={onDelete}
                                className="px-2.5 border border-white/10 hover:border-destructive hover:text-destructive transition-colors"
                                title="Delete"
                                data-testid={`ps-delete-${photo.id}`}
                            >
                                <Trash2 className="w-3 h-3" />
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const JobCard = ({ job, onRemove, onCategoryChange, onAddCategory, categories, resultUrl, busy }) => {
    const statusIcon = {
        pending: null,
        processing: <Loader2 className="w-4 h-4 text-storm-bolt animate-spin" />,
        done: <CheckCircle2 className="w-4 h-4 text-storm-bolt" />,
        error: <AlertCircle className="w-4 h-4 text-destructive" />,
    }[job.status];

    const handleCategorySelect = async (v) => {
        if (v === "__new_category__") {
            const slug = await onAddCategory();
            if (slug) onCategoryChange(slug);
            return;
        }
        onCategoryChange(v);
    };

    return (
        <div
            data-testid={`ps-job-${job.id}`}
            className="relative bg-storm-void border border-white/10 overflow-hidden group flex flex-col"
        >
            <div className="aspect-square relative overflow-hidden">
                <img
                    src={resultUrl || job.previewUrl}
                    alt={job.file.name}
                    className="w-full h-full object-cover"
                />
                {job.status === "processing" && (
                    <div className="absolute inset-0 bg-storm-void/70 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 text-storm-bolt animate-spin" />
                    </div>
                )}
                {job.status === "done" && (
                    <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-storm-bolt text-black px-2 py-0.5 text-[10px] uppercase tracking-wider font-heading">
                        <CheckCircle2 className="w-3 h-3" /> Done
                    </div>
                )}
                {job.status === "error" && (
                    <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-destructive text-white px-2 py-0.5 text-[10px] uppercase tracking-wider font-heading">
                        <AlertCircle className="w-3 h-3" /> Failed
                    </div>
                )}
                {!busy && job.status !== "processing" && (
                    <button
                        onClick={onRemove}
                        className="absolute top-1.5 right-1.5 w-6 h-6 inline-flex items-center justify-center bg-storm-void/80 border border-white/15 text-white hover:bg-destructive hover:border-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove from queue"
                        data-testid={`ps-job-remove-${job.id}`}
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
            <div className="p-2 flex items-center gap-2">
                {statusIcon}
                <span className="text-[11px] text-white/80 truncate flex-1">{job.file.name}</span>
            </div>
            <div className="px-2 pb-2">
                <Select value={job.category || "apparel"} onValueChange={handleCategorySelect} disabled={job.status === "processing"}>
                    <SelectTrigger
                        data-testid={`ps-job-cat-${job.id}`}
                        className="bg-storm-navy border-white/10 text-white rounded-none h-8 text-[11px] font-heading uppercase tracking-wider"
                    >
                        <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent className="bg-storm-navy border-white/10 text-white rounded-none">
                        {categories.map((c) => (
                            <SelectItem
                                key={c.slug}
                                value={c.slug}
                                className="focus:bg-storm-bolt focus:text-black rounded-none font-heading uppercase tracking-wider text-xs"
                            >
                                {c.name}
                            </SelectItem>
                        ))}
                        <SelectItem
                            value="__new_category__"
                            data-testid={`ps-job-cat-new-${job.id}`}
                            className="focus:bg-storm-bolt focus:text-black rounded-none font-heading uppercase tracking-wider text-xs text-storm-bolt"
                        >
                            + Add new category…
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>
            {job.error && (
                <p className="text-[10px] text-destructive px-2 pb-2 line-clamp-2">{job.error}</p>
            )}
        </div>
    );
};

export default PhotoStudioPage;
